import React from "react";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";

/**
 * Hook to parse and format text with quote styling
 * @param {Object} options - Configuration options
 * @param {string} options.text - The text content to parse
 * @param {string} options.quoteTextParts - JSON array or comma-separated list of text parts to style as quotes
 * @param {string} options.quoteStyle - The style class to apply to quote parts
 * @param {string} options.bodyStyle - The style class to apply to regular text parts
 * @returns {Function} A function that returns an array of styled paragraphs
 */
export const useQuoteParser = ({
    text,
    quoteTextParts,
    quoteStyle = "b3",
    bodyStyle = "b3"
}) => {
    const renderParsedText = React.useCallback(() => {
        if (!text) return null;
        
        // Parse quote parts (JSON array or comma-separated)
        let quoteParts = [];
        if (quoteTextParts && quoteTextParts.trim() !== '') {
            try {
                quoteParts = JSON.parse(quoteTextParts);
                if (!Array.isArray(quoteParts)) throw new Error();
            } catch {
                quoteParts = quoteTextParts.split(",").map((p) => p.trim()).filter(Boolean);
            }
        }
        
        // No quote parts to process
        if (!quoteParts.length) {
            return (
                <div className={bodyStyle}>
                    <p dangerouslySetInnerHTML={{ __html: text }} />
                </div>
            );
        }

        // Create a div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        
        // Process each paragraph
        const paragraphs = [];
        if (tempDiv.querySelectorAll('p').length > 0) {
            // Handle rich text with multiple paragraphs
            Array.from(tempDiv.querySelectorAll('p')).forEach((p, index) => {
                let html = p.innerHTML;
                
                // For each quote part, replace it with a styled version
                quoteParts.forEach(quotePart => {
                    // Create a regex that matches the exact quote part
                    const escQuotePart = quotePart.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const regex = new RegExp(`(${escQuotePart})`, 'g');
                    
                    // Check if this paragraph contains the quote part
                    if (p.textContent.includes(quotePart)) {
                        // Replace single quotes with double quotes in the matched text
                        const styledQuotePart = quotePart.replace(/'/g, '"');
                        
                        // Replace the matched text with styled version
                        html = html.replace(regex, `<span class="${quoteStyle}">${styledQuotePart}</span>`);
                    }
                });
                
                paragraphs.push(
                    <div key={index} className={bodyStyle}>
                        <p dangerouslySetInnerHTML={{ __html: html }} />
                    </div>
                );
            });
        } else {
            // Handle plain text or single paragraph
            let processedHTML = text;
            
            // Apply styling to each quote part
            quoteParts.forEach(quotePart => {
                // Create a regex that matches the exact quote part
                const escQuotePart = quotePart.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(${escQuotePart})`, 'g');
                
                // Check if the text contains this quote part
                if (tempDiv.textContent.includes(quotePart)) {
                    // Replace single quotes with double quotes in the matched text
                    const styledQuotePart = quotePart.replace(/'/g, '"');
                    
                    // Replace the matched text with styled version
                    processedHTML = processedHTML.replace(regex, `<span class="${quoteStyle}">${styledQuotePart}</span>`);
                }
            });
            
            paragraphs.push(
                <div key={0} className={bodyStyle}>
                    <p dangerouslySetInnerHTML={{ __html: processedHTML }} />
                </div>
            );
        }
        
        return paragraphs;
    }, [text, quoteTextParts, quoteStyle, bodyStyle]);
    
    return renderParsedText;
};

// Also export as default for backward compatibility
export default useQuoteParser;
